import { useCallback, useEffect, useState } from 'react';
import { FETCH_REFERRALS_PER_PAGE_COUNT } from '@bo-constants/member/members';
import type { FormattedMemberReferrals } from '@library/types/members/memberReferrals';
import { useGetMemberReferralsQuery } from '@bo-services/members/memberReferralsApi';
import { useImmer } from 'use-immer';
import { useParams } from 'react-router-dom';

export const useMemberReferrals = () => {
  const { memberId } = useParams<{ memberId?: string }>();
  const [allFetchedReferrals, setAllFetchedReferrals] =
    useImmer<FormattedMemberReferrals>([]);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const { data, isError, isFetching, isLoading } = useGetMemberReferralsQuery(
    {
      memberId,
      page: currentPage,
      perPage: FETCH_REFERRALS_PER_PAGE_COUNT
    },
    {
      skip: !memberId
    }
  );
  const isReferralsLoading = isFetching || isLoading;

  useEffect(() => {
    if (isError || isReferralsLoading) {
      return;
    }

    const memberReferrals = data || [];

    setAllFetchedReferrals((draft) => {
      draft.push(...memberReferrals);
    });

    if (memberReferrals.length === FETCH_REFERRALS_PER_PAGE_COUNT) {
      setTotalPages((prevTotalPages) => prevTotalPages + 1);
    }
  }, [data, isError, isReferralsLoading, setAllFetchedReferrals]);

  const handleCurrentPageChange = useCallback(() => {
    setCurrentPage((prevPage) => prevPage + 1);
  }, [setCurrentPage]);

  return {
    currentPage,
    onCurrentPageChange: handleCurrentPageChange,
    isError,
    isLoading: isReferralsLoading,
    memberReferrals: allFetchedReferrals,
    totalPages
  };
};
